import React, { createContext, useCallback, useMemo } from 'react';
import {
  closeSnackbar, MaterialDesignContent, SnackbarProvider, useSnackbar,
} from 'notistack';
import Close from '@mui/icons-material/Close';
import { IconButton, styled } from '@mui/material';

// Duration that info and success notifications are displayed.
const timeoutTwoHalfSeconds = 2500;
const timeoutFiveSeconds = 5000;

export const NotificationContext = createContext({
  /**
   * Push an info message to the snackbar notifications.
   * @param {string} _message - The message to display in the snackbar.
   * @returns {void}
   */
  pushInfo: (_message) => {},
  /**
   * Push a success message to the snackbar notifications.
   * @param {string} _message - The message to display in the snackbar.
   * @returns void
   */
  pushSuccess: (_message) => {},
  /**
   * Push an error message to the snackbar notifications.
   * @param {string} _message - The message to display in the snackbar.
   * @returns void
   */
  pushError: (_message) => {},
});

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
function NotificationProviderInner({ children }) {
  const { enqueueSnackbar } = useSnackbar();

  const pushInfo = useCallback((message) => {
    enqueueSnackbar(message, { variant: 'info', autoHideDuration: timeoutTwoHalfSeconds });
  }, [enqueueSnackbar]);

  const pushSuccess = useCallback((message) => {
    enqueueSnackbar(message, { variant: 'success', autoHideDuration: timeoutTwoHalfSeconds });
  }, [enqueueSnackbar]);

  const pushError = useCallback((message) => {
    enqueueSnackbar(message, { variant: 'error', autoHideDuration: timeoutFiveSeconds });
  }, [enqueueSnackbar]);

  const value = useMemo(() => ({
    pushInfo,
    pushSuccess,
    pushError,
  }), [pushInfo, pushSuccess, pushError]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

// notistack's own colours fail 4.5:1 and ignore the scheme. Each variant takes its status
// fill and MUI's ink for it, the pair tokens.test.js guards for status chips.
const StatusContent = styled(MaterialDesignContent)(({ theme }) => Object.fromEntries(
  ['success', 'error', 'warning', 'info'].map((key) => [`&.notistack-MuiContent-${key}`, {
    backgroundColor: (theme.vars || theme).palette[key].main,
    color: (theme.vars || theme).palette[key].contrastText,
  }]),
));

const STATUS_COMPONENTS = {
  success: StatusContent, error: StatusContent, warning: StatusContent, info: StatusContent,
};

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export default function NotificationProvider({ children }) {
  // `inherit`, not white: in dark the fills are light and take dark ink.
  const closeAction = (snackbarID) => (
    <IconButton
      onClick={() => closeSnackbar(snackbarID)}
      aria-label="Dismiss notification"
      sx={{ color: 'inherit' }}
    >
      <Close />
    </IconButton>
  );

  return (
    <SnackbarProvider
      anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
      action={(id) => closeAction(id)}
      SnackbarProps={{ style: { top: '90px' } }}
      Components={STATUS_COMPONENTS}
    >
      <NotificationProviderInner>{children}</NotificationProviderInner>
    </SnackbarProvider>
  );
}
